import { useQuery } from "@tanstack/react-query";
import { EmployeeService } from "../services/employee/employee.service";
import { IEmployee } from "../models";
import { useUser } from "./use-user";
import { DepartmentCode } from "../constants";
import { eApproveTitleCode } from "../constants/bizTitle";
import { useCallback } from "react";
import { DefaultOptionType } from "antd/lib/select";

interface IFilterEmployee {
  eBizTitleCodes?: string[];
  eLocations?: string[];
  eDepCodes?: string[];
  depCodes?: string[];
}

const branchList = [
  DepartmentCode.TSN,
  "TSN",
  "VTSN",
  DepartmentCode.NB,
  "NBA",
  "VNBA",
  DepartmentCode.DN,
  "DAD",
  "VDAD",
];

export const useEmployee = () => {
  const { email } = useUser();
  const { data = [], isLoading } = useQuery<Array<IEmployee>>({
    queryKey: ["employees"],
    queryFn: () => EmployeeService.getEmployees(),
  });

  const employeesHO = data.filter((e) => e.eDepartmentCode === "OAP");

  const currentEmployee = data.find((e) => e?.eMail?.toLowerCase() === email.toLowerCase());

  const getEmployeeManagerByUnitCodeOptions = (unitCode: string) => {
    return data
      .filter((e) => {
        return unitCode === e.eDepartmentCode && ["GD", "PGĐ"].includes(e.eBizTitleCode);
      })
      .map((e, index) => ({ ...e, eEmployeeId: index }));
  };

  const getEmployeesByDepartmentCode = (departmentCode: any) => {
    if (branchList.includes(departmentCode)) {
      return data.filter((e) => {
        if ([DepartmentCode.TSN, "TSN", "VTSN"].includes(departmentCode)) {
          return ["TSN", "VTSN"].includes(e.eLocation) && ["GD", "PGĐ"].includes(e.eBizTitleCode);
        }
        if ([DepartmentCode.NB, "NBA", "VNBA"].includes(departmentCode)) {
          return ["NBA", "VNBA"].includes(e.eLocation) && ["GD", "PGĐ"].includes(e.eBizTitleCode);
        }
        if ([DepartmentCode.DN, "DAD", "VDAD"].includes(departmentCode)) {
          return ["DAD", "VDAD"].includes(e.eLocation) && ["GD", "PGĐ"].includes(e.eBizTitleCode);
        }
        return false;
      });
    }
    if (departmentCode === "KCQ") {
      return data.filter((e) => {
        return ["GD", "PGĐ"].includes(e.eBizTitleCode) && !branchList.includes(departmentCode);
      });
    }
    return data.filter((e) => e.eDepartmentCode === departmentCode);
  };

  const getEmployeesByEmail = (email: string) => {
    return data.find((e) => e?.eMail?.toLowerCase() === email?.toLowerCase()) || null;
  };

  const getEmployeeByBizTitle = () => {
    return data.filter((e) => {
      return eApproveTitleCode.indexOf(e.eBizTitleCode);
    });
  };

  // getEmployees(true) to get all values
  const getEmployees = useCallback(
    (filterField: IFilterEmployee | true) => {
      if (filterField === true) return data;
      const { eBizTitleCodes, eLocations, eDepCodes, depCodes } = filterField;
      return data.filter(({ eBizTitleCode, eLocation, eDepartmentCode }) => {
        if (!eBizTitleCodes && !eLocations && !eDepCodes && !depCodes) {
          return false;
        }
        const isInEDepCodes = eDepCodes ? eDepCodes.includes(eDepartmentCode) : true;
        const isInBizTitleCodes = eBizTitleCodes ? eBizTitleCodes.includes(eBizTitleCode) : true;
        const isInLocations = eLocations ? eLocations.includes(eLocation) : true;
        return isInLocations && isInBizTitleCodes && isInEDepCodes;
      });
    },
    [data],
  );

  const getApproverOptions = useCallback(
    (filterField: IFilterEmployee | true): DefaultOptionType[] => {
      if (filterField === true) return data.map((e) => ({ key: e.eEmployeeId, label: e.Title, value: e.eMail }));
      const { eBizTitleCodes, eLocations, eDepCodes, depCodes } = filterField;
      return data
        .filter(({ eBizTitleCode, eLocation, eDepartmentCode }) => {
          if (!eBizTitleCodes && !eLocations && !eDepCodes && !depCodes) {
            return false;
          }
          const isInEDepCodes = eDepCodes ? eDepCodes.includes(eDepartmentCode) : true;
          const isInBizTitleCodes = eBizTitleCodes ? eBizTitleCodes.includes(eBizTitleCode) : true;
          const isInLocations = eLocations ? eLocations.includes(eLocation) : true;
          return isInLocations && isInBizTitleCodes && isInEDepCodes;
        })
        .map((e) => ({ key: e.eEmployeeId, label: e.Title, value: e.eMail }));
    },
    [data],
  );

  return {
    data,
    isLoading,
    employeesHO,
    currentEmployee,
    getEmployeeManagerByUnitCodeOptions,
    getEmployeesByDepartmentCode,
    getEmployeeByBizTitle,
    getEmployeesByEmail,
    getEmployees,
    getApproverOptions,
  };
};
